import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { QuestionnaireItem } from '@medplum/fhirtypes'
import { Form, Radio, Select, Space, Typography } from 'antd'
import styled from 'styled-components'
import Link from 'next/link'
import { AnswersLayout } from 'components/Assessments/components/types'
import {
  HalfWidthContainer,
  ItemWithConditionalQuestions,
  ItemWithTextDetailsContainer
} from 'components/Assessments/components/styles'
import RadioField from 'components/Assessments/components/RadioField'
import SanitizedTextArea from 'components/Common/Input/SanitizedTextArea'
import {
  AssessmentInfoTooltip,
  isEmptyTooltipContent,
  ScoringTooltipMarkdown
} from 'components/Common/AssessmentInfoTooltip'
import { getCssSelectorForLinkId } from 'utils/helpers'
import { findComponentExtensionAndReturnDisplay } from 'components/Assessments/helpers'
import { getSectionReferenceId } from 'components/Assessments/components/helpers'
import AssessmentFormContext from 'contexts/AssessmentFormContext'
import { QuestionnaireAnswerType } from 'components/Assessments/assessmentType'
import { useGenerateAnswerFromSectionResults } from 'components/Assessments/PatAssessment/hooks/useGenerateAnswerFromSectionResults'
import { SECTION_DEPENDENT_HELP_TEXT } from 'components/Assessments/PatAssessment/constants'
import { DEFAULT_REQUIRED_FIELD_MESSAGE } from 'components/Assessments/constants'
import {
  buildFormRules,
  codingToCodingValue,
  createMultiSelectValueFromAnswerOptions,
  createMultiSelectValuePropsFromAnswerOptions,
  createRadioValueFromEvent,
  createRadioValueProps,
  detectDetailedMode,
  extractCodeFromFormValue,
  extractDescriptionAndOptions,
  getFieldLinkId,
  getFieldText,
  isCodingValue,
  isValidateFormValuesType,
  parseHelperTexts,
  parseScoringTooltipMarkdown,
  shouldShowSectionIncompleteMessage,
  shouldShowSectionReferenceLink
} from './utils'
import { CodingValue } from './types'

const { Text, Paragraph } = Typography

export interface AdditionalQuestionItemProps {
  mainQuestionFieldName: string
  additionalQuestion: QuestionnaireItem
  additionalFormFieldName: string
  disabled: boolean
  assessmentMustBeFinalized: boolean
  inQA: boolean
  inQAEdit: boolean
  disableHover: boolean
  isFocused: boolean
  onCommentClick: () => void
}

/**
 * Component for rendering additional question as a separate question item
 * Shows/hides based on main question value
 */
function AdditionalQuestionItem({
  mainQuestionFieldName,
  additionalQuestion,
  additionalFormFieldName,
  disabled,
  assessmentMustBeFinalized,
  inQA,
  inQAEdit,
  disableHover,
  isFocused,
  onCommentClick
}: AdditionalQuestionItemProps): JSX.Element | null {
  const form = Form.useFormInstance()

  // Watch the main question value for conditional rendering
  const mainQuestionValue = Form.useWatch(mainQuestionFieldName, form) as CodingValue | undefined

  // Show additional question if main question is not "0"
  const shouldShow = mainQuestionValue?.code && mainQuestionValue.code !== '0'

  const getValueFromEvent = useCallback(
    (value: string[]) =>
      createMultiSelectValueFromAnswerOptions(additionalQuestion.answerOption)(value),
    [additionalQuestion.answerOption]
  )

  const getValueProps = useCallback(
    (value: CodingValue[] | undefined) => createMultiSelectValuePropsFromAnswerOptions()(value),
    []
  )

  const selectOptions = useMemo(
    () =>
      additionalQuestion.answerOption
        ?.filter((option) => option?.valueCoding?.code)
        .map((option) => ({
          label: option?.valueCoding?.display,
          value: option?.valueCoding?.code
        })),
    [additionalQuestion.answerOption]
  )

  if (!additionalQuestion || !additionalFormFieldName) {
    return null
  }

  if (!shouldShow) {
    return null
  }

  return (
    <ItemWithTextDetailsContainer
      id={getCssSelectorForLinkId(additionalQuestion.linkId)}
      inQA={inQA}
      inQAEdit={inQAEdit}
      disableHover={disableHover}
      isFocused={isFocused}
      onClick={onCommentClick}
    >
      <HalfWidthContainer>
        <AdditionalQuestionLabel strong>{additionalQuestion.text}</AdditionalQuestionLabel>
      </HalfWidthContainer>
      <HalfWidthContainer>
        <StyledFormItem
          name={additionalFormFieldName}
          rules={[
            {
              required: Boolean(assessmentMustBeFinalized && additionalQuestion.required),
              message: DEFAULT_REQUIRED_FIELD_MESSAGE
            }
          ]}
          labelCol={{ span: 0 }}
          wrapperCol={{ span: 24 }}
          colon={false}
          getValueFromEvent={getValueFromEvent}
          getValueProps={getValueProps}
        >
          <FullWidthSelect
            mode="multiple"
            disabled={disabled}
            placeholder="Type or select an option"
            options={selectOptions}
            showSearch
          />
        </StyledFormItem>
      </HalfWidthContainer>
    </ItemWithTextDetailsContainer>
  )
}

export interface CodingRadioQuestionWithNotesProps {
  field: QuestionnaireItem
  formFieldName: string
  notesField?: QuestionnaireItem
  notesFormFieldName?: string
  disabled?: boolean
  required?: boolean
  customRules?: Array<{ required: boolean; message: string }>
  inQA: boolean
  inQAEdit: boolean
  disableHover: boolean
  isFocused: boolean
  onCommentClick: () => void
  /**
   * Optional: Force detailed mode (with prefix, descriptions, numbered labels)
   * If not provided, auto-detects based on field extensions
   */
  detailedMode?: boolean
  additionalQuestion?: QuestionnaireItem
  additionalFormFieldName?: string
  assessmentMustBeFinalized?: boolean
}

/**
 * Supports two modes:
 * - Simple mode: Horizontal radio buttons, simple title
 * - Detailed mode: Vertical radio buttons with numbered labels, prefix in title,
 *   description and detailed option descriptions in left column
 */
function CodingRadioQuestionWithNotes({
  field,
  formFieldName,
  notesField,
  notesFormFieldName,
  disabled = false,
  required = false,
  customRules = [],
  inQA,
  inQAEdit,
  disableHover,
  isFocused,
  onCommentClick,
  detailedMode
}: CodingRadioQuestionWithNotesProps): JSX.Element {
  const { form } = useContext(AssessmentFormContext)
  const allRules = buildFormRules(required, field.required, customRules)

  // Get section reference ID for "Click to view section" link
  const sectionReferenceId = getSectionReferenceId(field)
  const initialSelected = field.answerOption?.find((option) => option?.initialSelected)
  const isDisabledQuestionWithSectionReference = Boolean(
    sectionReferenceId && field.readOnly && initialSelected
  )

  // Determine if field should be disabled (explicitly disabled or auto-calculated from section reference)
  const shouldDisableField = disabled || isDisabledQuestionWithSectionReference

  // Auto-detect detailed mode: check if field has helper-text extension with multiple entries
  const helperTexts = parseHelperTexts(field)
  const prefix = field.prefix || ''
  const isDetailedMode = detectDetailedMode(helperTexts, field.prefix, detailedMode)

  // Extract prefix and text
  const questionText = getFieldText(field)
  const questionLinkId = getFieldLinkId(field)

  // Extract description and detailed option descriptions (for detailed mode)
  const { description, optionDescriptions } = extractDescriptionAndOptions(
    helperTexts,
    isDetailedMode
  )

  // Get section completion status for validation error message
  const { generatedItemValue, isSectionForItemComplete } = useGenerateAnswerFromSectionResults({
    questionType: field.type as QuestionnaireAnswerType,
    questionLinkId,
    questionText,
    questionAnswerOptions: field.answerOption,
    questionInitialSelected: initialSelected
  })

  const computedOverrideValue = useMemo(() => {
    if (!generatedItemValue || !isDisabledQuestionWithSectionReference || !form) {
      return null
    }
    if (!isValidateFormValuesType(generatedItemValue)) {
      return null
    }
    const value = generatedItemValue.value
    if (!isCodingValue(value)) {
      return null
    }
    if (!value.code) {
      return null
    }

    // Convert Coding to CodingValue format expected by getValueProps
    return codingToCodingValue(value)
  }, [generatedItemValue, isDisabledQuestionWithSectionReference, form])

  // Set form field value when computed override value changes
  useEffect(() => {
    if (computedOverrideValue && form && isDisabledQuestionWithSectionReference) {
      const currentValue = form.getFieldValue(formFieldName)
      const currentCode = extractCodeFromFormValue(currentValue)
      if (currentCode !== computedOverrideValue.code) {
        form.setFieldValue(formFieldName, computedOverrideValue)
      }
    }
  }, [computedOverrideValue, form, formFieldName, isDisabledQuestionWithSectionReference])

  const getValueFromEvent = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | string) =>
      createRadioValueFromEvent(field.answerOption)(e),
    [field.answerOption]
  )

  // Function for extracting code from CodingValue
  // When field is disabled with computed override, return computed value instead of form value
  const getValueProps = useMemo(() => {
    const baseGetValueProps = createRadioValueProps()
    return (value: CodingValue | undefined) => {
      if (isDisabledQuestionWithSectionReference && computedOverrideValue) {
        return { value: computedOverrideValue.code }
      }
      return baseGetValueProps(value)
    }
  }, [computedOverrideValue, isDisabledQuestionWithSectionReference])

  const normalizeValue = useCallback(
    (
      value: { code?: string; display?: string } | undefined
    ): { code?: string; display?: string } | undefined => {
      if (computedOverrideValue && isDisabledQuestionWithSectionReference) {
        const currentCode = extractCodeFromFormValue(value)
        const overrideCode = computedOverrideValue.code

        if (currentCode !== overrideCode) {
          return computedOverrideValue
        }
      }

      return value
    },
    [computedOverrideValue, isDisabledQuestionWithSectionReference]
  )

  // Section incomplete error message
  const sectionIncompleteHelpText = shouldShowSectionIncompleteMessage(
    isDisabledQuestionWithSectionReference,
    isSectionForItemComplete
  ) ? (
    <Text style={{ color: 'red', display: 'block', marginTop: '8px' }}>
      {SECTION_DEPENDENT_HELP_TEXT[questionLinkId]}
    </Text>
  ) : null

  // Render section reference link if applicable
  const sectionReferenceLink = shouldShowSectionReferenceLink(
    isDisabledQuestionWithSectionReference,
    sectionReferenceId
  ) ? (
    <Link href={`#${sectionReferenceId}`}>
      <Text underline style={{ display: 'block', marginTop: '8px' }}>
        Click to view section
      </Text>
    </Link>
  ) : null

  // Build radio options
  const { radioOptions, radioOptionsForDisplay } = useMemo(() => {
    const options = field.answerOption?.map((option) => {
      const code = option?.valueCoding?.code ?? ''
      const display = option?.valueCoding?.display ?? ''
      const value = code || display
      const label = isDetailedMode ? (
        <>
          <Text strong>{code}</Text> - {display}
        </>
      ) : (
        display
      )
      return { code, display, value, label }
    })

    return {
      radioOptions: options,
      radioOptionsForDisplay: isDetailedMode
        ? undefined
        : options?.map((opt) => ({ label: opt.label as string, value: opt.value }))
    }
  }, [field.answerOption, isDetailedMode])

  // Notes placeholder
  const notesPlaceholder = notesField?.extension
    ? findComponentExtensionAndReturnDisplay(notesField.extension)
    : 'Add a comment'

  const scoringTooltipMarkdown = useMemo(() => parseScoringTooltipMarkdown(field), [field])

  return (
    <ItemWithTextDetailsContainer
      id={getCssSelectorForLinkId(field.linkId)}
      inQA={inQA}
      inQAEdit={inQAEdit}
      disableHover={disableHover}
      isFocused={isFocused}
      onClick={onCommentClick}
    >
      <HalfWidthContainer>
        <QuestionTitleRow $isDetailedMode={isDetailedMode}>
          <QuestionTitle strong>
            {prefix ? `${prefix} ${questionText}` : questionText}
          </QuestionTitle>
          {!isEmptyTooltipContent(scoringTooltipMarkdown) && (
            <ScoringTooltipAnchor>
              <AssessmentInfoTooltip
                content={<ScoringTooltipMarkdown text={scoringTooltipMarkdown} />}
              />
            </ScoringTooltipAnchor>
          )}
        </QuestionTitleRow>
        {description && (
          <Paragraph type="secondary" style={{ marginBottom: '16px' }}>
            {description}
          </Paragraph>
        )}
        {isDetailedMode &&
          optionDescriptions.map((text, index) => (
            <Paragraph key={index} type="secondary" style={{ marginBottom: '8px' }}>
              {text}
            </Paragraph>
          ))}
        {sectionReferenceLink}
      </HalfWidthContainer>
      <HalfWidthContainer>
        <StyledFormItemWithBottomMargin
          name={formFieldName}
          rules={allRules}
          $hasNotes={Boolean(notesFormFieldName)}
          getValueFromEvent={getValueFromEvent}
          getValueProps={getValueProps}
          normalize={normalizeValue}
          validateTrigger={['onChange', 'onBlur']}
        >
          {isDetailedMode ? (
            <Radio.Group disabled={shouldDisableField}>
              <Space direction="vertical">
                {radioOptions?.map((option, index) => (
                  <Radio key={index} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          ) : (
            <RadioField
              id={formFieldName}
              disabled={shouldDisableField}
              options={radioOptionsForDisplay}
              layout={AnswersLayout.HORIZONTAL}
            />
          )}
        </StyledFormItemWithBottomMargin>
        {sectionIncompleteHelpText}
        {notesFormFieldName && (
          <StyledFormItemWithTopMargin name={notesFormFieldName}>
            <SanitizedTextArea
              disabled={disabled}
              placeholder={notesPlaceholder}
              autoSize={{ minRows: 4, maxRows: 8 }}
            />
          </StyledFormItemWithTopMargin>
        )}
      </HalfWidthContainer>
    </ItemWithTextDetailsContainer>
  )
}

function CodingRadioQuestionWithNotesWrapper(
  props: CodingRadioQuestionWithNotesProps
): JSX.Element {
  const {
    additionalQuestion,
    additionalFormFieldName,
    disabled,
    assessmentMustBeFinalized,
    inQA,
    inQAEdit,
    disableHover,
    isFocused,
    onCommentClick
  } = props

  const hasAdditionalQuestion = additionalQuestion && additionalFormFieldName
  const { form } = useContext(AssessmentFormContext)

  // Watch the main question value to determine if additional question should be shown
  const mainQuestionValue = Form.useWatch(props.formFieldName, form) as CodingValue | undefined
  const shouldShowAdditional = mainQuestionValue?.code && mainQuestionValue.code !== '0'

  // Watch the additional field value to check if it has data
  // Only watch if additionalFormFieldName exists
  const watchedAdditionalValue = Form.useWatch((additionalFormFieldName || '') as string, form)
  const additionalFieldValue = additionalFormFieldName ? watchedAdditionalValue : undefined

  // Clear form field value when additional question is hidden
  // Prevents stale data from persisting when main question is set to "0"
  useEffect(() => {
    if (
      !shouldShowAdditional &&
      mainQuestionValue?.code === '0' &&
      additionalFieldValue &&
      additionalFormFieldName &&
      form
    ) {
      form.setFieldsValue({ [additionalFormFieldName]: undefined })
    }
  }, [shouldShowAdditional, mainQuestionValue, additionalFieldValue, additionalFormFieldName, form])

  if (!hasAdditionalQuestion) {
    return <CodingRadioQuestionWithNotes {...props} />
  }

  return (
    <ItemWithConditionalQuestions
      inQA={inQA}
      inQAEdit={inQAEdit}
      disableHover={disableHover}
      isFocused={isFocused}
      onClick={onCommentClick}
    >
      <CodingRadioQuestionWithNotes {...props} />
      <AdditionalQuestionItem
        mainQuestionFieldName={props.formFieldName}
        additionalQuestion={additionalQuestion}
        additionalFormFieldName={additionalFormFieldName}
        disabled={disabled ?? false}
        assessmentMustBeFinalized={assessmentMustBeFinalized ?? false}
        inQA={inQA ?? false}
        inQAEdit={inQAEdit ?? false}
        disableHover={disableHover}
        isFocused={isFocused}
        onCommentClick={onCommentClick}
      />
    </ItemWithConditionalQuestions>
  )
}

const AdditionalQuestionLabel = styled(Text)`
  font-size: 15px;
  display: block;
  text-decoration: underline;
`

const StyledFormItem = styled(Form.Item)`
  margin: 0;
`

const StyledFormItemWithTopMargin = styled(Form.Item)`
  margin: 0;
  margin-top: 18px;
`

const StyledFormItemWithBottomMargin = styled(Form.Item)<{ $hasNotes?: boolean }>`
  margin: 0;
  margin-bottom: ${(props) => (props.$hasNotes ? '16px' : '0')};
`

const FullWidthSelect = styled(Select)`
  width: 100%;
`

const QuestionTitleRow = styled.div<{ $isDetailedMode: boolean }>`
  display: flex;
  align-items: flex-start;
  align-self: flex-start;
  gap: 8px;
  margin-bottom: ${(props) => (props.$isDetailedMode ? '8px' : '0')};
  max-width: 100%;
`

const ScoringTooltipAnchor = styled.span`
  flex-shrink: 0;
  line-height: 0;
`

const QuestionTitle = styled(Text)`
  font-size: 16px;
  display: block;
  margin-bottom: 0;
`

export default CodingRadioQuestionWithNotesWrapper
